/**
 * Synchronisation des préférences avec le portail AuthSEmply.
 *
 * Le thème et l'accessibilité étaient LOCAUX à chaque produit (stockage du
 * navigateur, propre au domaine) : une palette choisie dans Compta ne suivait
 * pas dans Previ. Ils vivent désormais au portail, où vit le compte —
 * `GET /me/preferences`, `PUT /me/preferences/theme`,
 * `PUT /me/preferences/accessibility`.
 *
 * Les contrôleurs (controller.js, a11y.js) ne connaissent pas le portail : ils
 * reçoivent un `remote` — deux fonctions, `load` et `save` — et c'est tout.
 * Cette fabrique en construit un par famille de réglage à partir de ce que
 * chaque produit sait déjà : l'origine du portail (`issuer()`) et la façon
 * d'obtenir un jeton (`jetonValide()`, ou `userManager.getUser()`).
 *
 * Règles de synchronisation, communes aux deux contrôleurs :
 *
 *   - le stockage local reste la MÉMOIRE IMMÉDIATE : il est relu avant la
 *     première peinture (script anti-flash) et appliqué au démarrage. Le
 *     portail ne fait que le réaligner, quand il répond ;
 *   - à `sync()`, ce que dit le portail L'EMPORTE — c'est la préférence du
 *     compte, et l'onglet n'est qu'un de ses écrans. Un portail qui n'a rien
 *     (compte sans préférence, ou pas encore connecté) ne touche à rien ;
 *   - chaque réglage est réécrit au portail, sans attendre la réponse et sans
 *     bloquer l'interface : un thème est un confort, jamais un motif d'échec.
 *     Les écritures sont regroupées (300 ms) pour ne pas mitrailler le
 *     portail quand quelqu'un essaie trois palettes d'affilée.
 */

const CHEMIN = '/me/preferences';

async function appel(baseUrl, getToken, chemin, init) {
  const jeton = await getToken();
  // Sans jeton, pas de compte : rien à lire, rien à écrire. Ce n'est pas une
  // erreur — le visiteur n'est simplement pas connecté.
  if (!jeton) return null;
  const response = await fetch(`${baseUrl}${chemin}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${jeton}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init?.headers ?? {}),
    },
  });
  if (!response.ok) throw new Error(`Portail : ${response.status}`);
  return response.status === 204 ? null : response.json();
}

/**
 * @param {object} options
 * @param {string} options.baseUrl  Origine du portail, sans chemin (l'issuer OIDC).
 * @param {() => Promise<string|null>} options.getToken  Jeton d'accès courant, ou null.
 */
export function createPortalPreferences({ baseUrl, getToken }) {
  const base = String(baseUrl ?? '').replace(/\/$/, '');

  // Une seule lecture partagée par les deux contrôleurs : ils démarrent
  // ensemble, inutile de demander deux fois la même ligne au portail.
  let lecture = null;
  function lire() {
    if (!lecture) {
      lecture = appel(base, getToken, CHEMIN).finally(() => {
        // La réponse n'est gardée que le temps d'un démarrage : un `sync()`
        // ultérieur (reconnexion) doit relire.
        setTimeout(() => {
          lecture = null;
        }, 0);
      });
    }
    return lecture;
  }

  return {
    theme: {
      load: async () => (await lire())?.theme ?? null,
      save: (state) =>
        appel(base, getToken, `${CHEMIN}/theme`, {
          method: 'PUT',
          body: JSON.stringify(state),
        }),
    },
    a11y: {
      load: async () => (await lire())?.accessibility ?? null,
      save: (state) =>
        appel(base, getToken, `${CHEMIN}/accessibility`, {
          method: 'PUT',
          body: JSON.stringify(state),
        }),
    },
  };
}

/**
 * Enregistrement différé et silencieux vers un `remote`.
 *
 * Partagé par les deux contrôleurs : regroupe les écritures rapprochées et
 * avale les échecs — la préférence reste appliquée et en stockage local, elle
 * ne survivra simplement pas au changement d'appareil tant que le portail ne
 * répond pas.
 */
export function createRemoteWriter(remote, { delay = 300 } = {}) {
  let timer = null;
  let dernier = null;
  return {
    schedule(state) {
      if (!remote?.save) return;
      dernier = state;
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        timer = null;
        const s = dernier;
        dernier = null;
        Promise.resolve()
          .then(() => remote.save(s))
          .catch(() => {
            // Le portail n'a pas pris l'écriture : rien à faire ici, le
            // stockage local garde la préférence pour cet appareil.
          });
      }, delay);
    },
    /** Envoie tout de suite ce qui attend (fermeture d'onglet, tests). */
    flush() {
      if (!timer) return;
      clearTimeout(timer);
      timer = null;
      const s = dernier;
      dernier = null;
      return Promise.resolve()
        .then(() => remote.save(s))
        .catch(() => {});
    },
  };
}
